import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Trash2, CheckCircle2, ArrowLeft, Mail, Lock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import useSEO from '../lib/useSEO';

const DeleteAccountPage = () => {
  const { toast } = useToast();
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [cfg, setCfg] = useState({});

  useSEO({
    title: 'Delete Your Account — TelanganaMart',
    description: 'Permanently delete your TelanganaMart account and personal data. Sign in with your registered mobile number and password to delete instantly, or contact our support team.',
  });

  useEffect(() => { api.getFooterSettings().then(setCfg).catch(() => setCfg({})); }, []);
  const supportEmail = cfg.contact_email || 'support@telanganamart.com';

  const deleteAccount = async () => {
    const m = mobile.replace(/\D/g, '');
    if (m.length !== 10) return toast({ title: 'Enter a valid 10-digit mobile number', variant: 'destructive' });
    if (!password) return toast({ title: 'Enter your password', variant: 'destructive' });
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      return toast({ title: 'Type DELETE in capital letters to confirm', variant: 'destructive' });
    }
    setBusy(true);
    try {
      await api.deleteAccount({ mobile: m, password, reason });
      setDone(true);
    } catch (err) {
      toast({ title: 'Deletion failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4" data-testid="delete-account-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#2c54b2] hover:underline mb-4"><ArrowLeft className="w-4 h-4" /> Back to home</Link>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-5">
            <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2"><ShieldAlert className="w-6 h-6" /> Delete Your Account</h1>
            <p className="text-sm text-red-100 mt-1">Permanently delete your TelanganaMart account and personal data.</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Info */}
            <div className="text-sm text-gray-700 space-y-3">
              <p>This page lets you permanently delete your <b>TelanganaMart</b> account. To protect your account, sign in below with your registered <b>mobile number and password</b>.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                  <p className="font-semibold text-red-800 text-sm mb-1">Data that is deleted</p>
                  <ul className="list-disc list-inside text-xs text-gray-700 space-y-0.5">
                    <li>Your profile (name, mobile, email, city)</li>
                    <li>Saved preferences & wishlists</li>
                    <li>In-app notifications</li>
                    <li>Login access to your account</li>
                  </ul>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="font-semibold text-gray-800 text-sm mb-1">Data we may retain</p>
                  <ul className="list-disc list-inside text-xs text-gray-600 space-y-0.5">
                    <li>Enquiries/requirements already sent to vendors</li>
                    <li>Invoices & transaction records (legal/tax compliance)</li>
                    <li>Anonymised, aggregated analytics</li>
                  </ul>
                </div>
              </div>
              <p className="text-xs text-gray-500">Deletion is permanent and cannot be undone. Retained records are kept only as required by law and are not used to re-identify you.</p>
            </div>

            {!done ? (
              <div className="space-y-4 border-t border-gray-100 pt-5">
                <div>
                  <Label className="text-sm">Registered mobile number</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded text-sm text-gray-600">+91</span>
                    <Input
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit mobile"
                      className="max-w-xs"
                      data-testid="delete-mobile-input"
                    />
                  </div>
                </div>
                <div className="max-w-xs">
                  <Label className="text-sm">Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your account password"
                    data-testid="delete-password-input"
                  />
                </div>
                <div className="max-w-md">
                  <Label className="text-sm">Reason (optional)</Label>
                  <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Help us improve (optional)" data-testid="delete-reason-input" />
                </div>
                <div className="max-w-xs">
                  <Label className="text-sm text-red-700">Type <span className="font-mono bg-red-50 border border-red-200 px-1 rounded">DELETE</span> to confirm</Label>
                  <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" className="bg-white" data-testid="delete-confirm-input" />
                </div>
                <Button onClick={deleteAccount} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> {busy ? 'Deleting…' : 'Permanently delete my account'}
                </Button>
                <p className="text-[11px] text-gray-400 flex items-start gap-1"><Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Haven&apos;t set a password? Open the app → Profile → Account &amp; Security to set one, then return here — or email us below.</p>
              </div>
            ) : (
              <div className="border-t border-gray-100 pt-6 text-center space-y-3" data-testid="delete-success">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                <h2 className="text-lg font-bold text-gray-900">Your account has been deleted</h2>
                <p className="text-sm text-gray-600 max-w-md mx-auto">Your TelanganaMart account and personal data have been permanently removed. We&apos;re sorry to see you go.</p>
                <Link to="/"><Button variant="outline" className="mt-2">Return to homepage</Button></Link>
              </div>
            )}

            {/* Help */}
            <div className="border-t border-gray-100 pt-4 text-xs text-gray-500">
              <p className="flex items-center gap-1.5 flex-wrap">
                <Mail className="w-3.5 h-3.5" /> Need help or can&apos;t sign in? Email us at{' '}
                <a href={`mailto:${supportEmail}?subject=Account%20Deletion%20Request`} className="text-[#2c54b2] hover:underline" data-testid="delete-support-email">{supportEmail}</a>
                {' '}and we&apos;ll process your deletion request within 7 days.
              </p>
              <p className="mt-1">You can also delete your account from inside the app: <b>Profile → Delete My Account</b>.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
